import {
  Box,
  Button,
  Chip,
  ListItemDecorator,
  Stack,
  TabList,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/joy";
import { Outlet, useChildMatches } from "@tanstack/react-router";
import { useMemo } from "react";

import Warning from "@/assets/icons/Warning";
import { useCompetitionQuery, useUpdateCompetitionMutation } from "@/hooks/competitions";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { TabLink } from "../Links";
import getPublishWarnings, { useHideWarningsMap } from "./getPublishWarnings";
import PublishResultsAlert from "./PublishResultsAlert";

const useTabFromPath = <TTabList extends string[]>({
  basePath,
  tabs,
  defaultTab,
}: {
  basePath: string;
  tabs: TTabList;
  defaultTab: TTabList[number];
}): TTabList[number] => {
  const tab = useChildMatches({
    select: (matches) => {
      return matches[0]?.routeId.replace(basePath + "/", "");
    },
  });
  return tab && tabs.includes(tab) ? tab : defaultTab;
};

export default function CompetitionProfile({
  competitionUuid,
  children,
}: {
  competitionUuid?: string;
  children?: React.ReactNode;
}) {
  const { data: competition } = useCompetitionQuery(competitionUuid);

  const publishWarnings = useMemo(() => getPublishWarnings(competition), [competition]);

  const tab = useTabFromPath({
    basePath: "/gym/$venueUuid/events/$competitionUuid/profile",
    tabs: ["details", "categories", "circuits", "scoring", "results"] as const,
    defaultTab: "details",
  });

  const venueUuid = useCurrentVenueUuid();
  const publishMutation = useUpdateCompetitionMutation(
    competitionUuid,
    venueUuid,
    competition?.league?.uuid,
  );

  const hidePublishWarningsMap = useHideWarningsMap(competitionUuid);

  const publishButton = (
    <Button
      size="lg"
      color="primary"
      disabled={publishWarnings.allWarnings.length > 0}
      loading={publishMutation.isPending}
      loadingPosition="start"
      onClick={() =>
        publishMutation.mutate(
          { isHidden: false },
          {
            onError: () => alert("Unknown error occurred. Please try again or contact support"),
          },
        )
      }
    >
      Publish
    </Button>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: "neutral.200",
        padding: 1.5,
      }}
    >
      {competition?.isHidden ? (
        <Stack
          sx={{
            border: "2px solid",
            borderColor: "neutral.400",
            backgroundColor: "white",
            maxWidth: 800,
            borderRadius: 8,
            padding: 1.5,
            marginBottom: 2,
          }}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Chip size="lg" sx={{ fontWeight: "bold" }} variant="solid">
              Draft mode
            </Chip>
            <Typography color="neutral" level="body-sm">
              The competition is only visible to staff members
            </Typography>
          </Box>
          {publishWarnings.allWarnings.length ? (
            <Tooltip
              // enabled={publishWarnings.allWarnings.length > 0}
              title="To publish the competition, finish going through the tabs and fix any issues"
              size="md"
              placement="bottom"
            >
              <Box>{publishButton}</Box>
            </Tooltip>
          ) : (
            publishButton
          )}
        </Stack>
      ) : null}
      <PublishResultsAlert />
      <Stack
        sx={{
          backgroundColor: "white",
          borderRadius: 8,
          overflow: "hidden",
          flex: 1,
          maxWidth: 800,
        }}
      >
        <Tabs
          value={tab}
          sx={{
            paddingX: 1.5,
            paddingTop: 0.5,
            ".MuiTab-root": {
              flex: 1,
            },
          }}
        >
          <TabList>
            <TabLink
              value="details"
              to={competitionUuid ? "/gym/$venueUuid/events/$competitionUuid/profile/details" : "."}
              replace
            >
              {!hidePublishWarningsMap["details"] && publishWarnings.byTab["details"] ? (
                <ListItemDecorator>
                  <Warning />
                </ListItemDecorator>
              ) : null}
              Details
            </TabLink>
            <TabLink
              value="categories"
              from="/gym/$venueUuid/events/$competitionUuid/profile"
              to="./categories"
              replace
              disabled={!competitionUuid}
            >
              {!hidePublishWarningsMap["categories"] && publishWarnings.byTab["categories"] ? (
                <ListItemDecorator>
                  <Warning />
                </ListItemDecorator>
              ) : null}
              Categories
            </TabLink>
            <TabLink
              value="circuits"
              from="/gym/$venueUuid/events/$competitionUuid/profile"
              to="./circuits"
              replace
              disabled={!competitionUuid}
            >
              {!hidePublishWarningsMap["circuits"] && publishWarnings.byTab["circuits"] ? (
                <ListItemDecorator>
                  <Warning />
                </ListItemDecorator>
              ) : null}
              Circuits
            </TabLink>
            <TabLink
              value="scoring"
              from="/gym/$venueUuid/events/$competitionUuid/profile"
              to="./scoring"
              replace
              disabled={!competitionUuid}
            >
              {!hidePublishWarningsMap["scoring"] && publishWarnings.byTab["scoring"] ? (
                <ListItemDecorator>
                  <Warning />
                </ListItemDecorator>
              ) : null}
              Scoring
            </TabLink>
          </TabList>
        </Tabs>
        {children || <Outlet />}
      </Stack>
    </Box>
  );
}
